import { DEFER_COLLAB_SEND } from '@atlaskit/editor-common/collab-defer-collab-send';
import { Slice } from '@atlaskit/editor-prosemirror/model';
import type { EditorState, Transaction } from '@atlaskit/editor-prosemirror/state';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';

import { isReviewMomentEnabled } from '../../../ui/utils/review-moment-enabled';
import { resolveAgentAttribution } from '../../agent-attribution/resolveAgentAttribution';
import { aiSessionContextPluginKey } from '../../ai-session-context/ai-session-context-plugin-key';
import {
	AI_FINAL_STREAMING_TRANSFORMATION_DATA,
	CAPTURE_ABORTED_TOOL_CALL_POSITIONS,
} from '../../ai-session-context/meta-keys';
import { aiExperienceDecorationPluginKey } from '../../decoration/decoration-plugin-key';
import {
	isImproveDescriptionSyncBlockFixEnabled,
	withoutSyncBlocks,
} from '../../improve-description-sync-block';
import { purePmInEditorDirectStreamingPluginKey } from '../pluginKey';
import { updateStreamPositions, metaTransaction } from '../transforms';
import type { PurePmInEditorDirectStreamingPluginState } from '../types';
import type { FragmentHealthFailure } from '../utils/adfConversion';
import {
	resolveContentFragment,
	describeFragmentHealth,
	fragmentIsHealthy,
	calculateTelepointerPos,
} from '../utils/adfConversion';
import { clampPosition } from '../utils/positionValidation';
import { applyCellReplacementAttributes } from './cellAttributeAdjustments';
import { applyReplaceNodeLocalIdFix } from './localIdAdjustments';
import {
	calculateListAdjustment,
	calculateBlockMarkAdjustment,
	calculateEmptySelectionAdjustment,
	calculateRangeSelectionAdjustment,
} from './positionAdjustments';
import { mapStartPos, mapToPos } from './positionMapping';
import {
	calculateSliceOpenStartEndAdjustment,
	calculateSyncBlockAdjustment,
} from './sliceAdjustments';
import { reviewSlicesForReplaceRange } from './slicesMatchingReplaceRange';
import { stampUnifiedAgentEditChrome } from './stampUnifiedAgentEditChrome';

/**
 * Builds transaction for committing final AI content.
 * This is Phase 2 of the two-dispatch finalize pattern.
 * Should be called AFTER restore transaction has been dispatched.
 *
 * Note: This closely matches the original commitFinalResponse in useInEditorDirectStreaming.
 * The slice adjustment (openStart/openEnd) is applied to prevent unwanted node merging.
 */
/**
 * Describes what `buildCommitTransaction` did, so callers can report the cases
 * where the AI content never reached the document.
 */
export type CommitOutcome =
	| { applied: true }
	| {
			applied: false;
			/** Populated when the final fragment existed but failed `node.check()`. */
			health?: FragmentHealthFailure;
			reason:
				| 'noSessionState'
				| 'noStreamingState'
				| 'noPositions'
				| 'noRange'
				| 'unhealthyFragment'
				| 'noFragment'
				| 'commitBuildError';
	  };

export function buildCommitTransaction(
	state: EditorState,
	reportOutcome?: (outcome: CommitOutcome) => void,
): Transaction | null {
	const sessionState = aiSessionContextPluginKey.getState(state);
	if (!sessionState) {
		reportOutcome?.({ applied: false, reason: 'noSessionState' });
		return null;
	}
	const { adfResponse, markdown, idMap, responseVersion } = sessionState;

	const streamingState = purePmInEditorDirectStreamingPluginKey.getState(state);
	const decorationPluginState = aiExperienceDecorationPluginKey.getState(state);
	const { positions } = decorationPluginState ?? {};

	if (!streamingState) {
		reportOutcome?.({ applied: false, reason: 'noStreamingState' });
		return null;
	}

	// When decoration positions are unavailable (e.g. cleared after the restore transaction
	// remapped them away for `replaceNode` commands on empty table cells), we still need to
	// mark the stream as finalised so `isStreaming` returns to `false` and
	// `endExperiencePromise` can resolve. Fall through to `buildCommitStateOnly` which
	// sets `isFinalStream: true` without needing document positions.
	if (!positions) {
		reportOutcome?.({ applied: false, reason: 'noPositions' });
		return buildCommitStateOnly(state, streamingState, sessionState.responseVersion);
	}

	// After restore, the document is back to original state
	// Use the positions from streaming state (which were mapped through restore)
	const {
		startPos,
		userContentEndPos,
		originalUserContentSlice,
		reviewOriginalSlice,
		aiContentSlice,
		posAdjusted,
	} = streamingState;

	if (startPos === undefined || userContentEndPos === undefined) {
		reportOutcome?.({ applied: false, reason: 'noRange' });
		return buildCommitStateOnly(state, streamingState, responseVersion);
	}

	// Resolve final content using the prioritised conversion chain:
	// ADF → experiment-gated markdown → free-gen markdown fallback
	const isSyncBlockFixEnabled = isImproveDescriptionSyncBlockFixEnabled(
		decorationPluginState?.configItem?.key,
	);
	const resolvedFragment = resolveContentFragment(state.schema, adfResponse, markdown, idMap);
	const finalFragment =
		resolvedFragment && isSyncBlockFixEnabled
			? withoutSyncBlocks(resolvedFragment)
			: resolvedFragment;

	// IMPORTANT: Prefer the slice adjusted during the update phase when it still matches the
	// final adfResponse. This preserves the existing behavior for edge cases like inserting
	// a heading at the start of a paragraph. If the final response changed after the last
	// stream update, rebuild the slice from the final adfResponse so we don't commit stale
	// streamed content.
	const canReuseStreamedSlice =
		!!aiContentSlice &&
		!!finalFragment &&
		fragmentIsHealthy(finalFragment) &&
		aiContentSlice.content.eq(finalFragment);

	let slice = canReuseStreamedSlice ? aiContentSlice : undefined;

	if (!slice) {
		if (!finalFragment || !fragmentIsHealthy(finalFragment)) {
			// The dominant silent-content-loss path: the model produced ADF the
			// editor schema rejects, so the stream is dropped without an error.
			reportOutcome?.(
				finalFragment
					? {
							applied: false,
							reason: 'unhealthyFragment',
							health: describeFragmentHealth(finalFragment),
						}
					: { applied: false, reason: 'noFragment' },
			);
			return buildCommitStateOnly(state, streamingState, responseVersion);
		}

		slice = Slice.maxOpen(finalFragment);
		if (!slice.content.firstChild) {
			slice = Slice.empty;
		}
	}

	// Use positions from streaming state
	const docSize = state.doc.content.size;
	let workingStartPos = clampPosition(startPos, docSize);
	let workingUserContentEndPos = clampPosition(userContentEndPos, docSize);

	/**
	 * If value for tool call is received in one stream then
	 * 	buildUpdateTransaction can be called only once without fragment.
	 * And then buildCommitTransaction will be called for the final value.
	 *
	 * In such a case, adjustments for empty or range selection hasn't been
	 * 	calculated in buildUpdateTransaction.
	 * So if posAdjusted is false, we need to calculate the adjustments for empty or range selection.
	 */
	if (!posAdjusted) {
		const isEmptySelection = positions[0] === positions[1];

		if (isEmptySelection) {
			const adjustment = calculateEmptySelectionAdjustment({
				state,
				fragment: slice.content,
				startPos: workingStartPos,
				userContentEndPos: workingUserContentEndPos,
			});
			if (adjustment.adjusted) {
				workingStartPos = adjustment.startPos;
				workingUserContentEndPos = adjustment.userContentEndPos;
			}
		} else {
			const adjustment = calculateRangeSelectionAdjustment({
				state,
				fragment: slice.content,
				startPos: workingStartPos,
				userContentEndPos: workingUserContentEndPos,
			});
			if (adjustment.adjusted) {
				workingStartPos = adjustment.startPos;
			}
		}
	}

	// List adjustment (matching original)
	const listAdjustment = calculateListAdjustment({
		state,
		fragment: slice.content,
		startPos: workingStartPos,
		userContentEndPos: workingUserContentEndPos,
	});
	if (listAdjustment.adjusted) {
		workingStartPos = listAdjustment.startPos;
		workingUserContentEndPos = listAdjustment.userContentEndPos;
	}

	// Block mark adjustment — when startPos is inside a paragraph/heading's text
	// and the replacement is the same block type with marks, move startPos back
	// to include the block node so block-level marks (e.g. alignment) are applied.
	const blockMarkAdjustment = calculateBlockMarkAdjustment({
		state,
		fragment: slice.content,
		startPos: workingStartPos,
		userContentEndPos: workingUserContentEndPos,
	});
	if (blockMarkAdjustment.adjusted) {
		workingStartPos = blockMarkAdjustment.startPos;
	}

	if (isSyncBlockFixEnabled) {
		const syncBlockAdjustment = calculateSyncBlockAdjustment({
			state,
			fragment: slice.content,
			startPos: workingStartPos,
			toPos: workingUserContentEndPos,
			userContentEndPos: workingUserContentEndPos,
		});
		if (syncBlockAdjustment.adjusted) {
			workingUserContentEndPos = syncBlockAdjustment.toPos;
		}
	}

	let tr = state.tr;
	const streamTimeStamp = streamingState.streamTimeStamp;
	const safeEndPos = Math.max(workingStartPos, Math.min(workingUserContentEndPos, docSize));
	// Slice the original at the range we are about to replace — not the mount
	// selection, which can be inner inlines while list/block adjustments move
	// the replace onto the wrapper.
	const originalDocForReview = state.doc;

	// Adjust slice openStart/openEnd to prevent unwanted node merging
	// (e.g. losing a panel wrapper when inserting before an existing node)
	slice = calculateSliceOpenStartEndAdjustment({
		state,
		slice,
		startPos: workingStartPos,
		toPos: safeEndPos,
	});

	// Apply final content - matching original commitFinalResponse logic
	try {
		const { paragraph } = tr.doc.type.schema.nodes;
		// If we have a lone paragraph - replaceRange will not replace with a paragraph
		const isSingleParagraph =
			slice.content.firstChild === slice.content.lastChild &&
			slice.content.firstChild?.type === paragraph;
		let requireLocalIdFix = true;

		if (isSingleParagraph && workingStartPos < docSize) {
			const $from = tr.doc.resolve(workingStartPos);
			const isEmptyParagraph = $from.parent.type === paragraph && $from.parent.content.size === 0;

			if (isEmptyParagraph && slice.content.firstChild) {
				tr = tr.replaceRangeWith(workingStartPos, safeEndPos, slice.content.firstChild);
				requireLocalIdFix = false;
			} else {
				tr = tr.replaceRange(workingStartPos, safeEndPos, slice);
			}
		} else {
			tr = tr.replaceRange(workingStartPos, safeEndPos, slice);
		}

		if (requireLocalIdFix) {
			// When replaceRange merges inline content into an existing block of the same
			// type, the original block's attrs (including localId) are preserved. If the
			// replacement carries a different localId, patch it here so downstream
			// consumers (collab, comments, etc.) see the intended id. This also handles
			// slices that contain multiple top-level blocks — each replaced block has
			// its localId patched independently.
			tr = applyReplaceNodeLocalIdFix({
				tr,
				replacementContent: slice.content,
				startPos: workingStartPos,
			});
		}
		try {
			tr = applyCellReplacementAttributes({
				tr,
				originalDoc: state.doc,
				replacementContent: finalFragment ?? slice.content,
				adfResponse,
				startPos: workingStartPos,
				endPos: workingUserContentEndPos,
			});
		} catch {
			// Keep the committed content when optional cell styling cannot be restored.
		}

		// The stream is over: release the collab send hold so the commit and everything held
		// behind it goes out immediately.
		tr = tr.setMeta(DEFER_COLLAB_SEND, false);
		tr = metaTransaction({
			tr,
			isFinalStream: true,
			stackTime: streamTimeStamp,
			agentAttribution: resolveAgentAttribution({
				sessionAgentAttribution: sessionState.agentAttribution,
				configItemKey: decorationPluginState?.configItem?.key,
			}),
		});
	} catch {
		reportOutcome?.({ applied: false, reason: 'commitBuildError' });
		return buildCommitStateOnly(state, streamingState, responseVersion);
	}

	// Set selection at end of inserted content
	try {
		tr = tr.setSelection(
			TextSelection.create(tr.doc, Math.min(tr.selection.to, tr.doc.content.size)),
		);
	} catch {
		// Invalid position - keep default selection
	}

	// Skip history if content unchanged
	if (originalUserContentSlice) {
		try {
			const newDocSize = tr.doc.content.size;
			const finalStartPos = clampPosition(mapStartPos({ pos: startPos, tr }), newDocSize);
			const finalEndPos = clampPosition(mapToPos({ pos: userContentEndPos, tr }), newDocSize);
			const newSlice = tr.doc.slice(finalStartPos, finalEndPos);
			if (newSlice.eq(originalUserContentSlice)) {
				tr = tr.setMeta('addToHistory', false);
			}
		} catch {
			// Position mapping failed - add to history as normal
		}
	}

	// Map final positions for telepointer
	const finalDocSize = tr.doc.content.size;
	const mappedStartPos = clampPosition(mapStartPos({ pos: startPos, tr }), finalDocSize);
	const mappedToPos = clampPosition(mapToPos({ pos: userContentEndPos, tr }), finalDocSize);

	const telepointerPos = calculateTelepointerPos(tr.doc, mappedStartPos, mappedToPos);

	tr = updateStreamPositions({
		aiContentEndPos: mappedToPos,
		telepointerPos,
		isFinalStream: true,
		lastProcessedVersion: responseVersion,
	})(tr);

	// [CCI-15904] When the Review moment gate is ON, stamp this
	// transaction with the data needed by the Post Stream Review toolbar.
	// Slices are taken from the actual replace span: undo/redo
	// must not mix a mount-time inner slice with a later wrapper range.
	// The ai-session-context plugin's apply() reads this meta to append an entry
	// to `aiContentPositions`.
	// Skip recording reviewable `aiContentPositions` for a create stream — there is
	// nothing to review.
	if (isReviewMomentEnabled() && originalUserContentSlice && !sessionState.isCreateSession) {
		const review = reviewSlicesForReplaceRange({
			from: workingStartPos,
			newDoc: tr.doc,
			originalDoc: originalDocForReview,
			reviewOriginalSlice,
			to: safeEndPos,
		});
		// [CCI-17658] Determine if this commit intersects the reworked segment.
		// Any overlap merges with the existing segment (append slice); no overlap
		// would start a new review moment (out of scope — tracked in CCI-17980).
		const snap = sessionState.reworkSnapshot;
		const hasValidSnapshot =
			snap &&
			sessionState.streamReviewSegments?.length &&
			snap.topLevelIndex >= 0 &&
			snap.topLevelIndex < sessionState.streamReviewSegments.length;
		const intersects =
			hasValidSnapshot &&
			((review.startPos <= snap.startPos && review.endPos >= snap.endPos) ||
				(review.startPos >= snap.startPos && review.endPos <= snap.endPos) ||
				(review.startPos <= snap.startPos && review.endPos >= snap.startPos) ||
				(review.startPos <= snap.endPos && review.endPos >= snap.endPos));
		const reworkMutation =
			hasValidSnapshot && intersects ? { topLevelIndex: snap.topLevelIndex } : undefined;
		tr.setMeta(AI_FINAL_STREAMING_TRANSFORMATION_DATA, {
			newSlice: review.newSlice,
			originalUserContentSlice: review.originalSlice,
			positions: {
				mappedStartPos: review.startPos,
				mappedToPos: review.endPos,
			},
			reworkMutation,
		});
	}

	// If this is the terminal write of a tool call force-finished by the queue
	// timeout, stamp its final (doc-mapped) positions. ai-session-context's apply()
	// persists them as `abortedToolCallPositions[toolCallId]` for later resume.
	if (sessionState.pendingForceAbortedToolCallId) {
		tr.setMeta(CAPTURE_ABORTED_TOOL_CALL_POSITIONS, {
			toolCallId: sessionState.pendingForceAbortedToolCallId,
			startPos: mappedStartPos,
			aiContentEndPos: mappedToPos,
		});
	}

	tr = stampUnifiedAgentEditChrome(tr, state);

	reportOutcome?.({ applied: true });

	return tr;
}

function buildCommitStateOnly(
	state: EditorState,
	streamingState: PurePmInEditorDirectStreamingPluginState,
	responseVersion: number,
): Transaction {
	let tr = state.tr;
	tr = updateStreamPositions({
		aiContentEndPos: streamingState.aiContentEndPos ?? streamingState.startPos ?? 0,
		telepointerPos: streamingState.telepointerPos ?? streamingState.startPos ?? 0,
		isFinalStream: true,
		lastProcessedVersion: responseVersion,
	})(tr);

	// No document steps: retain existing cues without inventing a changed range.
	tr = stampUnifiedAgentEditChrome(tr, state);

	return tr;
}
